<template>
  <BittsContainer>
    <h2 :class="!allStoresReady || loading ? '' : 'mb-24'">Billing Summary</h2>
    <BillingErrorGuard :errored :loading="!allStoresReady || loading">
      <div class="cost-summary">
        <section
          class="cost-summary__section"
          data-testid="full-access"
          v-if="addedSeats"
        >
          <div>
            <span>{{ addedSeats }} x Full Access Seats</span>
            <p
              >{{ centsToDollars(costPerSeat) }} per seat/{{ billingPeriod }}</p
            >
          </div>
          <p>{{ centsToDollars(totalNewSeatCost) }}</p>
        </section>
        <section
          class="cost-summary__section"
          data-testid="prorated-adjustment"
          v-if="proratedFee"
        >
          <div class="flex-row gap-4">
            <span>Prorated Adjustment </span>
            <BittsTooltip>
              <FontAwesomeIcon
                :icon="['fak', 'information-circle']"
                class="text-neutral-accent text-12 pb-1"
              />
              <template #title>
                We only charge you for the time remaining in your subscription
              </template>
            </BittsTooltip></div
          >
          <p>-{{ centsToDollars(proratedFee) }}</p>
        </section>
        <section
          class="cost-summary__section font-bold text-m"
          data-testid="amount-due"
        >
          <div>Amount Due Today</div>
          <p class="text-success-text">{{ centsToDollars(amountDueToday) }}</p>
        </section>
        <CurrentPlanCollapse
          :billing-period
          :core-seat-limit
          :total-seat-cost="centsToDollars(currentSeatsTotalCost)"
          :platform-fee="centsToDollars(currentPlatformFee)"
          :seat-cost="centsToDollars(currentCostPerSeat)"
        />
        <div class="cost-summary__next-invoice">
          <span
            v-html="
              injectStrings(V5_BILLING_COPY.seatPurchase.alert, {
                values: injections,
                bold: true,
                italic: false,
                underline: false,
              })
            "
          />
        </div>
        <div class="flex flex-col gap-16">
          <BittsButton
            :disabled="errored"
            class="w-full"
            text="Add Seats"
            @click="emit('add-seats')"
          />
          <BittsButton
            class="w-full"
            text="Cancel"
            variant="ghost"
            @click="emit('cancel')"
          />
        </div>
      </div>
    </BillingErrorGuard>
  </BittsContainer>
</template>

<script setup lang="ts">
import { BittsButton, BittsContainer, BittsTooltip } from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';

import BillingErrorGuard from '@/components/billing/BillingErrorGuard.vue';
import CurrentPlanCollapse from '@/components/billing/CurrentPlanCollapse.vue';

import useBilling from '@/composables/useBilling';
import {
  BILLING_PLAN_ANNUAL,
  BILLING_PLAN_MONTHLY,
  V5_BILLING_COPY,
} from '@/constants/billing';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { centsToDollars, injectStrings } from '@/utils';

const {
  addedSeats,
  billingPeriod,
  coreSeatLimit,
  subscriptionNextBillingDate,
} = defineProps<{
  addedSeats: number;
  billingPeriod: string;
  coreSeatLimit: number;
  subscriptionNextBillingDate: string;
}>();

const emit = defineEmits<{
  (e: 'add-seats' | 'cancel'): void;
  (e: 'amount-due-today', amountDueToday: number): void;
}>();

const loading = ref(true);
const errored = ref(false);
const flashesStore = useFlashesStore();
const { allStoresReady, processCostDataV4, fetchSeatDataV4 } = useBilling();

const isMonthly = computed(() => billingPeriod === 'month');
const chosenPeriod = computed(() =>
  isMonthly.value ? BILLING_PLAN_MONTHLY : BILLING_PLAN_ANNUAL,
);

// preview
const nextInvoice = ref(0);
const amountDueToday = ref(0);
const proratedFee = ref(0);
const costPerSeat = ref(0);
const coreSeatsTotalCost = ref(0);
const injections = computed(() => [
  centsToDollars(nextInvoice.value),
  subscriptionNextBillingDate,
]);

// current plan
const currentCostPerSeat = ref(0);
const currentSeatsTotalCost = ref(0);
const currentPlatformFee = ref(480000); // TODO, get this from chargify

const totalNewSeatCost = computed(() => addedSeats * costPerSeat.value);

onMounted(() => {
  previewCost();
});
watch(
  () => addedSeats,
  () => {
    previewCost();
  },
);
async function previewCost() {
  try {
    errored.value = false;
    loading.value = true;

    const costData = await fetchSeatDataV4({
      newCoreSeats: addedSeats,
      newSalesSeats: 0,
      period: chosenPeriod.value,
    });

    const processedData = processCostDataV4({
      costData,
      newCoreSeats: addedSeats,
      newSalesSeats: 0,
      period: chosenPeriod.value,
    });

    nextInvoice.value = processedData.nextSubscriptionCost;
    proratedFee.value = processedData.proratedFee;
    amountDueToday.value = processedData.amountDueToday;
    emit('amount-due-today', amountDueToday.value);
    costPerSeat.value = processedData.costPerCoreSeat;
    coreSeatsTotalCost.value = processedData.coreSeatsTotalCost;
    loading.value = false;

    const currentCostData = await fetchSeatDataV4({
      newCoreSeats: coreSeatLimit,
      newSalesSeats: 0,
      period: chosenPeriod.value,
    });

    const currentProcessedData = processCostDataV4({
      costData: currentCostData,
      newCoreSeats: coreSeatLimit,
      newSalesSeats: 0,
      period: chosenPeriod.value,
    });

    currentCostPerSeat.value = currentProcessedData.costPerCoreSeat;
    currentSeatsTotalCost.value = currentProcessedData.coreSeatsTotalCost;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    errored.value = true;
    loading.value = false;
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'Could not preview cost',
      description: err.message,
    });
  }
}
</script>

<style scoped lang="pcss">
h2 {
  @apply font-bold text-neutral-text-strong text-lg;
}
.bitts-container {
  @apply rounded-bts-lg border-none p-24 bg-neutral-background-weak;
}
.cost-summary {
  @apply flex flex-col gap-24;
}
.cost-summary__next-invoice {
  @apply text-info-text bg-info-background-weak rounded-8 px-16 py-8 text-center;
}
p {
  @apply text-neutral-text-weak;
}
.cost-summary__section {
  @apply flex justify-between items-start border-b border-solid border-neutral-border pb-16;
  div {
    @apply flex flex-col text-neutral-text-strong;
  }
  &:last-of-type {
    @apply border-none pb-0;
  }
}
</style>
